import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { format, addDays } from 'date-fns';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import LoginToBook from '../components/LoginToBook';
import { useAuth } from '../App';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// ── Tiers ─────────────────────────────────────────────────
// `type` sent to the backend is `${tier}-${party}` — e.g. 'gold-solo'.
// Prices shown here are display-only; the backend is authoritative on the charge.
const TIERS = {
  gold: {
    key: 'gold',
    name: 'Biohack Gold',
    tagline: 'The full six-stage circuit',
    duration: '3–4 hours',
    accessible: false,
    protocol: [
      { step: '01', label: 'InBody 770 Scan & Health Consultation' },
      { step: '02', label: 'Red Light Therapy' },
      { step: '03', label: 'Open Gym Session' },
      { step: '04', label: 'Iglu Sauna', note: 'private · cold plunge + cold shower' },
      { step: '05', label: 'Cryotherapy', note: '−150°C' },
      { step: '06', label: 'Hyperbaric Oxygen Therapy', note: '93% O₂ at 2 ATA' },
    ],
    prices: { solo: { label: '£249' }, couple: { label: '£449' } },
  },
  silver: {
    key: 'silver',
    name: 'Biohack Silver',
    tagline: 'Four stages · fully accessible',
    duration: '2 hours',
    accessible: true,
    protocol: [
      { step: '01', label: 'InBody 770 Scan & Health Consultation' },
      { step: '02', label: 'Red Light Therapy' },
      { step: '03', label: 'Iglu Sauna' },
      { step: '04', label: 'Cryotherapy' },
    ],
    prices: { solo: { label: '£165' }, couple: { label: '£299' } },
  },
};

function PaymentForm({ type, priceLabel, startTime, onSuccess }) {
  const stripe   = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  async function handlePay() {
    setLoading(true); setError('');
    const { paymentMethod, error: pmErr } = await stripe.createPaymentMethod({
      type: 'card', card: elements.getElement(CardElement),
    });
    if (pmErr) { setError(pmErr.message); setLoading(false); return; }
    try {
      await api.createBiohack({ startTime, type, stripePaymentMethodId: paymentMethod.id });
      onSuccess();
    } catch (err) { setError(err.error || 'Booking failed. Please try again.'); }
    setLoading(false);
  }

  return (
    <div>
      <div style={{ background: 'var(--grey-100)', borderRadius: 10, padding: 16, marginBottom: 14 }}>
        <CardElement options={{ style: { base: { fontSize: '16px', color: '#1A1A1A' } } }} />
      </div>
      {error && <div className="alert alert-error">{error}</div>}
      <button className="btn btn-primary btn-full" onClick={handlePay} disabled={loading}>
        {loading ? 'Booking…' : `Pay ${priceLabel} & Book`}
      </button>
    </div>
  );
}

export default function BiohackPage() {
  const nav = useNavigate();
  const { session } = useAuth();
  const [params] = useSearchParams();

  const [tierKey, setTierKey] = useState(params.get('tier') === 'silver' ? 'silver' : 'gold');
  const [party, setParty]     = useState(params.get('type') === 'couple' ? 'couple' : 'solo');
  const [date, setDate]           = useState(format(new Date(), 'yyyy-MM-dd'));
  const [availability, setAvail]  = useState([]);
  const [selectedTime, setSelectedTime] = useState(null);
  const [loading, setLoading]     = useState(false);
  const [success, setSuccess]     = useState(false);

  const tier      = TIERS[tierKey];
  const bookType  = `${tierKey}-${party}`;               // e.g. 'gold-solo'
  const priceLbl  = tier.prices[party].label;

  useEffect(() => {
    setLoading(true);
    api.getBiohackAvailability(date, bookType).then(({ availability }) => {
      setAvail(availability); setSelectedTime(null); setLoading(false);
    });
  }, [date, bookType]);

  const dates = Array.from({ length: 56 }, (_, i) => format(addDays(new Date(), i), 'yyyy-MM-dd'));

  if (success) return (
    <div className="page" style={{ textAlign: 'center', paddingTop: 60 }}>
      <div style={{ fontSize: '3rem', marginBottom: 16 }}>⚗️</div>
      <h2 style={{ fontWeight: 800, marginBottom: 8 }}>You're booked in!</h2>
      <p style={{ color: 'var(--grey-700)', marginBottom: 8 }}>{tier.name} · {party === 'couple' ? 'For Two' : 'Solo'}</p>
      <p style={{ color: 'var(--grey-700)', marginBottom: 24 }}>{format(new Date(selectedTime), "EEEE d MMMM 'at' h:mm aaa")}</p>
      <p style={{ color: 'var(--grey-500)', fontSize: '0.85rem', marginBottom: 24 }}>Confirmation sent to your email. Arrive 5 minutes early.</p>
      <button className="btn btn-primary" onClick={() => nav('/')}>Back to Home</button>
    </div>
  );

  return (
    <Elements stripe={stripePromise}>
      <div className="page">
        {/* Biohack hero photo */}
        <div style={{
          position: 'relative', borderRadius: 16, overflow: 'hidden', height: 220, marginBottom: 20,
          backgroundImage: `url('https://mhqbsdexwcqmdchsviig.supabase.co/storage/v1/object/public/treatment-images/biohack.png')`,
          backgroundSize: 'cover', backgroundPosition: 'center',
        }}>
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.85) 100%)' }} />
          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '20px' }}>
            <h1 style={{ color: '#fff', fontWeight: 800, fontSize: '1.5rem', marginBottom: 6, letterSpacing: '-0.01em' }}>The Biohack</h1>
            <div style={{ color: 'rgba(255,255,255,0.88)', fontSize: '0.85rem', lineHeight: 1.5 }}>
              Two curated biohacking circuits. Every station sequenced and arranged for you.
            </div>
          </div>
        </div>

        {/* Tier chooser */}
        <div className="section-title">Choose Your Experience</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24 }}>
          {Object.values(TIERS).map(t => {
            const isSel = t.key === tierKey;
            return (
              <button key={t.key} onClick={() => setTierKey(t.key)} style={{
                textAlign: 'left', padding: 16, borderRadius: 14, cursor: 'pointer',
                border: `2px solid ${isSel ? 'var(--green)' : 'var(--grey-300)'}`,
                background: isSel ? 'var(--green-pale)' : '#fff',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                  <span style={{ fontWeight: 800, fontSize: '1.05rem', color: isSel ? 'var(--green)' : 'var(--black)' }}>{t.name}</span>
                  <span style={{ fontWeight: 700, fontSize: '0.85rem', color: 'var(--grey-700)' }}>from {t.prices.solo.label}</span>
                </div>
                <div style={{ fontSize: '0.8rem', color: 'var(--grey-500)', marginTop: 4 }}>
                  {t.tagline} · {t.duration}
                </div>
                {t.accessible && (
                  <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--green)', letterSpacing: '0.04em', marginTop: 6 }}>
                    ♿ FULLY ACCESSIBLE
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Protocol for the selected tier */}
        <div className="card" style={{ padding: 20, marginBottom: 20 }}>
          <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--green)', letterSpacing: '0.1em', marginBottom: 12 }}>
            {tier.name.toUpperCase()} · YOUR PROTOCOL
          </div>
          {tier.protocol.map(p => (
            <div key={p.step} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--grey-100)' }}>
              <span style={{ fontWeight: 800, color: 'var(--grey-300)', fontSize: '0.85rem', minWidth: 24 }}>{p.step}</span>
              <span style={{ fontWeight: 500 }}>
                {p.label}
                {p.note && <span style={{ color: 'var(--grey-500)', fontWeight: 400, fontSize: '0.8rem' }}> · {p.note}</span>}
              </span>
            </div>
          ))}
        </div>

        {/* Solo / For Two toggle — prices are tier-specific */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
          {[{ value: 'solo', label: `Solo — ${tier.prices.solo.label}` }, { value: 'couple', label: `For Two — ${tier.prices.couple.label}` }].map(opt => (
            <button key={opt.value} onClick={() => setParty(opt.value)} style={{
              flex: 1, padding: '12px 8px', borderRadius: 10, fontWeight: 700, fontSize: '0.9rem',
              border: `2px solid ${party === opt.value ? 'var(--green)' : 'var(--grey-300)'}`,
              background: party === opt.value ? 'var(--green-pale)' : '#fff',
              color: party === opt.value ? 'var(--green)' : 'var(--grey-700)', cursor: 'pointer',
            }}>{opt.label}</button>
          ))}
        </div>

        {/* Date picker */}
        <div className="section-title">Choose a Date</div>
        <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 8, marginBottom: 20, scrollbarWidth: 'none' }}>
          {dates.map(d => {
            const isSelected = d === date;
            const dateObj = new Date(d + 'T12:00:00');
            return (
              <button key={d} onClick={() => setDate(d)} style={{
                minWidth: 52, padding: '10px 6px', borderRadius: 10, textAlign: 'center', flexShrink: 0,
                border: `2px solid ${isSelected ? 'var(--green)' : 'var(--grey-300)'}`,
                background: isSelected ? 'var(--green-pale)' : '#fff', cursor: 'pointer',
              }}>
                <div style={{ fontSize: '0.6rem', fontWeight: 700, color: isSelected ? 'var(--green)' : 'var(--grey-500)', textTransform: 'uppercase' }}>{format(dateObj, 'EEE')}</div>
                <div style={{ fontSize: '1rem', fontWeight: 800, color: isSelected ? 'var(--green)' : 'var(--black)' }}>{format(dateObj, 'd')}</div>
                <div style={{ fontSize: '0.6rem', fontWeight: 700, color: isSelected ? 'var(--green)' : 'var(--grey-500)', textTransform: 'uppercase' }}>{format(dateObj, 'MMM')}</div>
              </button>
            );
          })}
        </div>

        {/* Time slots */}
        <div className="section-title">Available Start Times</div>
        {loading ? <div className="loading-center"><div className="spinner" /></div> : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 24 }}>
            {availability.map(({ startTime: st, available }) => {
              const isSel = selectedTime === st;
              const t = new Date(st);
              return (
                <button key={st} onClick={() => available && setSelectedTime(st)} disabled={!available} style={{
                  padding: '12px 4px', borderRadius: 10, textAlign: 'center', fontWeight: 700,
                  border: `2px solid ${isSel ? 'var(--green)' : available ? 'var(--grey-300)' : 'var(--grey-100)'}`,
                  background: isSel ? 'var(--green-pale)' : '#fff',
                  color: isSel ? 'var(--green)' : available ? 'var(--black)' : 'var(--grey-300)',
                  cursor: available ? 'pointer' : 'not-allowed', opacity: available ? 1 : 0.4,
                }}>
                  <div style={{ fontSize: '0.9rem' }}>{format(t, 'h:mm')}</div>
                  <div style={{ fontSize: '0.6rem', fontWeight: 400, color: isSel ? 'var(--green)' : 'var(--grey-500)' }}>{format(t, 'aaa')}</div>
                </button>
              );
            })}
          </div>
        )}

        {/* Booking form — guests are prompted to log in instead */}
        {selectedTime && (
          <div className="card" style={{ padding: 20 }}>
            <h3 style={{ fontWeight: 700, marginBottom: 4 }}>Confirm {tier.name}</h3>
            <p style={{ color: 'var(--grey-500)', fontSize: '0.85rem', marginBottom: 4 }}>
              {party === 'couple' ? 'For Two' : 'Solo'} · {format(new Date(selectedTime), "EEEE d MMMM 'at' h:mm aaa")}
            </p>
            <p style={{ color: 'var(--grey-500)', fontSize: '0.8rem', marginBottom: 16 }}>
              Approx. {tier.duration} · All stations pre-booked for you
            </p>
            {session
              ? <PaymentForm type={bookType} priceLabel={priceLbl} startTime={selectedTime} onSuccess={() => setSuccess(true)} />
              : <LoginToBook message="Log in or create a free account to book your Biohack." />}
          </div>
        )}
      </div>
    </Elements>
  );
}
